export const hasOwnPropertyTS = <X extends object, Y extends PropertyKey>(obj: X, prop: Y): obj is X & Record<Y, unknown> => {
	return obj.hasOwnProperty(prop)
}

export const unknownHasOwnPropertyTS = <X extends unknown, Y extends PropertyKey>(obj: X, prop: Y): obj is X & Record<Y, unknown> => {
	return obj && typeof obj === 'object' && obj.hasOwnProperty(prop)
}


export const isStringProperty = <K extends keyof T, T>(host: T, property: K): host is T & Record<K, string> => {
	return typeof host[property] === "string";
}

export const isPropertyKey = (objectValue: unknown): objectValue is PropertyKey => {
	if (!objectValue) return false;
	return typeof objectValue === "number" || typeof objectValue === 'string' || typeof objectValue === 'symbol';
}

export const setKnownStringProperty = <K extends PropertyKey>(host: Record<K, string>, property: K, value: string): Record<K, string> => {
	host[property] = value;
	return host;
}


export const isNumberProperty = <K extends keyof T, T>(host: T, property: K): host is T & Record<K, number> => {
	return typeof host[property] === "number";
}

export const setKnownNumberProperty = <K extends PropertyKey>(host: Record<K, number>, property: K, value: number): Record<K, number> => {
	host[property] = value;
	return host;
}

export const getPropertyName = <T extends object>(obj: T, expression: (x: { [Property in keyof T]: () => string }) => () => string): string => {
	const res: { [Property in keyof T]: () => string } = {} as { [Property in keyof T]: () => string };
	Object.keys(obj).map(k => res[k as keyof typeof res] = () => k);
	return expression(res)();
}


export function containsKeys<T extends object>(object: Partial<T>, keys: Array<string | keyof T>): object is Required<T> {
	if (!object || !keys || keys.length === 0) return false;
	if (keys.every((key) => key in object)) return true;
	return false;
}


export function getObjectEntries<T extends object>(object: T): Array<[keyof T, T[keyof T]]> {
	if (!object) return [];

	let entries = Object.entries(object) as Array<[keyof T, T[keyof T]]>;
	return entries;
}


export function getObjectValues<T extends object>(object: T): Array<T[keyof T]> {
	if (!object) return [];

	let values = Object.values(object) as Array<T[keyof T]>;
	return values;
}

export function valueExist<T>(value: T | null | undefined): value is T {
	return value !== undefined && value !== null;
}

export function isValidKey<T extends object>(key: string, obj: T): key is keyof T & string {
	return typeof key === 'string' && key in obj;
}


export type ValueOf<T> = T[keyof T];

export type ObjectEntry<T> = [keyof T, T[keyof T]];

export type ObjectEntries<T> = Array<ObjectEntry<T>>;

export type ObjectKeys<T> = Array<keyof T>;

export type ObjectValues<T> = Array<ValueOf<T>>;



export type ParamIntersection<T> = { [K in keyof T]: (x: T[K]) => void }[keyof T] extends (x: infer I) => void ? I : never;

export type ParamMerged<T> = { [K in keyof T]: (x: T[K]) => void }[keyof T] extends (x: infer I) => void ? { [K in keyof I]: I[K] } : never;



export const updateObjectProperty = <T extends object, U extends PropertyKey>(object: T, property: U, value: any): T | null => {
	if (!hasOwnPropertyTS(object, property)) return null
	
	let originProperty = object[property];
	if (originProperty !== null && !(typeof originProperty === typeof value)) return null;
	
	if (originProperty !== null && typeof originProperty === "object") {
		let originPropertyConstructor = Object.getPrototypeOf(originProperty).constructor;
		if (!(value instanceof originPropertyConstructor)) return null;
	};
	let cloneObject = { ...object };
	cloneObject[property] = value;
	return cloneObject;
}

type Enumerate<N extends number, Acc extends number[] = []> = Acc['length'] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc['length']]>

export type IntRange<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>>

export const isIntRange = <TMinLimit extends number, TMaxLimit extends number>(value: number, minLimit: TMinLimit, maxLimit: TMaxLimit): value is IntRange<TMinLimit,TMaxLimit> => {
	return value >= minLimit && value <= maxLimit ? true : false;
}


type GrowToSize<T, N extends number, A extends T[]> = A['length'] extends N ? A : GrowToSize<T, N, [...A, T]>;

export type FixedArray<T, N extends number> = GrowToSize<T, N, []>;

// Utility type to capitalize a string
export type CapitalizeString<S extends string> =
	S extends `${infer F}${infer R}`
    ? `${Uppercase<F>}${R}`
    : S;


// Generic type to extract enum keys as capitalized strings
export type ExtractEnumKeys<T> = {
    [K in keyof T as
		T[K] extends number
		? CapitalizeString<string & K>
		: never
	]: CapitalizeString<string & K>
}

export type StrictOmit<T, K extends keyof T> = Omit<T, K>;


//JSON.stringify(object, null, 2)

// disable one row (without spaces)
// @ts - ignore: Unreachable code error

// disable all (without spaces)
// @ts - nocheck

// error pass, else others throw typescript error (without spaces)
// @ts - expect-error